(() => {
  const ROOT_SELECTOR = '[data-legal-page]';

  function meaningfulChildren(element) {
    return [...element.childNodes].filter((node) => {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent.trim() !== '';
      if (node.nodeType !== Node.ELEMENT_NODE) return false;
      return node.tagName !== 'BR' || node.nextSibling;
    });
  }

  function legacyHeading(element) {
    const children = meaningfulChildren(element);
    const firstElement = children.find((node) => node.nodeType === Node.ELEMENT_NODE && node.tagName !== 'BR');
    if (!firstElement || !firstElement.matches('strong, b')) return null;

    const label = firstElement.textContent.replace(/\s+/g, ' ').trim();
    if (!label || label.length > 120) return null;

    if (element.tagName === 'LI' && element.parentElement?.tagName === 'OL') return { element, label };

    if (element.tagName === 'P') {
      const nextElement = firstElement.nextElementSibling;
      const isStandalone = element.textContent.replace(/\s+/g, ' ').trim() === label;
      const isHeadingLead = nextElement?.tagName === 'BR' || firstElement.nextSibling?.nodeName === 'BR';
      if (isStandalone || isHeadingLead) return { element, label };
    }

    return null;
  }

  function collectHeadings(copy) {
    const blocks = [...copy.querySelectorAll('h2, h3, h4, p, ol > li')];
    return blocks.flatMap((element) => {
      if (/^H[2-4]$/.test(element.tagName)) {
        const label = element.textContent.replace(/\s+/g, ' ').trim();
        return label ? [{ element, label }] : [];
      }

      const legacy = legacyHeading(element);
      return legacy ? [legacy] : [];
    });
  }

  function slugify(value) {
    return value
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '') || 'section';
  }

  function createLink(entry) {
    const link = document.createElement('a');
    link.className = 'pp-legal-page__contents-link';
    link.href = `#${entry.id}`;
    link.dataset.legalTarget = entry.id;
    link.textContent = entry.label;
    return link;
  }

  function addEntry(list, entry) {
    const item = document.createElement('li');
    item.appendChild(createLink(entry));
    list.appendChild(item);
  }

  function activateLinks(root, id) {
    root.querySelectorAll('[data-legal-target]').forEach((link) => {
      const active = link.dataset.legalTarget === id;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'location');
      else link.removeAttribute('aria-current');
    });
  }

  function bindAnchorLinks(root) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    root.querySelectorAll('[data-legal-target]').forEach((link) => {
      link.addEventListener('click', (event) => {
        const id = link.dataset.legalTarget;
        const target = document.getElementById(id);
        if (!target) return;

        event.preventDefault();
        target.scrollIntoView({ behavior: reducedMotion ? 'auto' : 'smooth', block: 'start' });
        if (window.location.hash !== `#${id}`) window.history.pushState(null, '', `#${id}`);

        const mobileContents = root.querySelector('[data-legal-mobile-contents]');
        if (mobileContents) mobileContents.open = false;
        activateLinks(root, id);
      });
    });
  }

  function bindActiveSection(root, entries) {
    if (!('IntersectionObserver' in window)) return;

    const observer = new IntersectionObserver((observed) => {
      const visible = observed
        .filter((item) => item.isIntersecting)
        .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0];
      if (visible) activateLinks(root, visible.target.dataset.legalSectionId);
    }, { rootMargin: '-15% 0px -70% 0px' });

    entries.forEach((entry) => {
      entry.element.dataset.legalSectionId = entry.id;
      observer.observe(entry.element);
    });
  }

  function bindProgress(root) {
    const progress = root.querySelector('[data-legal-progress]');
    if (!progress) return;

    let frame = null;
    const update = () => {
      frame = null;
      const start = root.offsetTop;
      const available = Math.max(root.offsetHeight - window.innerHeight, 1);
      const amount = Math.min(1, Math.max(0, (window.scrollY - start) / available));
      progress.style.width = `${amount * 100}%`;
    };
    const schedule = () => {
      if (frame === null) frame = window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    update();
  }

  function initialise(root) {
    if (root.dataset.legalInitialised === 'true') return;
    root.dataset.legalInitialised = 'true';

    const copy = root.querySelector('[data-legal-copy]');
    const shell = root.querySelector('[data-legal-shell]');
    const desktopContents = root.querySelector('[data-legal-desktop-contents]');
    const mobileContents = root.querySelector('[data-legal-mobile-contents]');
    const desktopList = root.querySelector('[data-legal-desktop-list]');
    const mobileList = root.querySelector('[data-legal-mobile-list]');
    const showContents = root.dataset.showContents === 'true';
    if (!copy || !shell) return;

    const usedIds = new Set();
    const entries = collectHeadings(copy).map((entry, index) => {
      entry.element.classList.add('pp-legal-page__section-heading');
      let id = `legal-section-${index + 1}-${slugify(entry.label)}`;
      let suffix = 2;
      while (usedIds.has(id) || document.getElementById(id)) id = `${id}-${suffix++}`;
      usedIds.add(id);

      const anchor = document.createElement('span');
      anchor.id = id;
      anchor.className = 'pp-legal-page__anchor';
      anchor.setAttribute('aria-hidden', 'true');
      entry.element.before(anchor);
      return { ...entry, id, anchor };
    });

    if (showContents && entries.length && desktopContents && mobileContents && desktopList && mobileList) {
      entries.forEach((entry) => {
        addEntry(desktopList, entry);
        addEntry(mobileList, entry);
      });
      desktopContents.hidden = false;
      mobileContents.hidden = false;
      bindAnchorLinks(root);
      bindActiveSection(root, entries);
    } else {
      shell.classList.add('pp-legal-page__shell--without-contents');
    }

    const invalidLinks = [...root.querySelectorAll('[data-legal-target]')]
      .filter((link) => !document.getElementById(link.dataset.legalTarget));
    if (invalidLinks.length) console.error('Polypouch legal page: a contents link has no matching target.');

    bindProgress(root);

    if (window.location.hash) {
      const target = document.getElementById(window.location.hash.slice(1));
      if (target && root.contains(target)) {
        window.requestAnimationFrame(() => target.scrollIntoView({ block: 'start' }));
      }
    }
  }

  function initialiseAll(scope = document) {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initialise);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialiseAll());
  } else {
    initialiseAll();
  }

  document.addEventListener('shopify:section:load', (event) => initialiseAll(event.target));
})();

